import { useEffect, useMemo, useRef, useState } from "react";
import { Drawer, DrawerRef } from "../../../components/drawers/Drawer";
import { TechWorkingHoursModel } from "../../accounts/models/TechWorkingHoursModel";
import { formatDuration, getTechAvailability } from "../../accounts/utils/techAvailability";
import { useHub } from "../../../core/Global";
import { API_ROUTES } from "../../../core/Constants";
import FlexRow from "../../../components/base/FlexRow";
import Icon from "../../../components/base/Icon";
import Indicator from "../../../components/base/Indicator";


interface IProps {
    techId: string
    onClose: () => void
}
interface IWeekDay {
    id: number
    name: string
    short: string
}

export default function TechWorkingHoursDrawer(props: IProps) {

    const hub = useHub()
    const drawer = useRef<DrawerRef>(null)

    const [model, modelSet] = useState<TechWorkingHoursModel>()
    const [loading, loadingSet] = useState<boolean>(true)
    const [now, nowSet] = useState<Date>(new Date())

    useEffect(() => {
        hub.Get(`${API_ROUTES.Technicians}workinghours/${props.techId}`, res => {
            modelSet(res.data)
            loadingSet(false)
        })
    }, [props.techId])

    // Live clock ticking every second for the technician's local time
    useEffect(() => {
        const timer = setInterval(() => nowSet(new Date()), 1000)
        return () => clearInterval(timer)
    }, [])

    const [WeekDays] = useState<IWeekDay[]>([
        { id: 1, name: "Monday", short: "Mon" },
        { id: 2, name: "Tuesday", short: "Tue" },
        { id: 3, name: "Wednesday", short: "Wed" },
        { id: 4, name: "Thursday", short: "Thu" },
        { id: 5, name: "Friday", short: "Fri" },
        { id: 6, name: "Saturday", short: "Sat" },
        { id: 0, name: "Sunday", short: "Sun" },
    ])

    const pad = (n: number) => n.toString().padStart(2, "0")

    const { alwaysAvailable, techDate, openNow, secondsUntil } =
        useMemo(() => getTechAvailability(model, now), [model, now])

    const liveTime = techDate
        ? `${pad(techDate.getUTCHours())}:${pad(techDate.getUTCMinutes())}:${pad(techDate.getUTCSeconds())}`
        : undefined

    const techDay = techDate ? techDate.getUTCDay() : undefined

    return (
        <Drawer ref={drawer} title="Working Hours" titleIcon="calendar-clock"
            subtitle={model?.timezoneName ?? model?.timezoneId}
            onClose={props.onClose}>

            <style>{`
                .wh-time { font-variant-numeric: tabular-nums; }
                .wh-day + .wh-day { border-top: 1px solid var(--bs-gray-200); }
                .wh-always-badge {
                    width: 84px; height: 84px; border-radius: 50%;
                    background: var(--bs-light-success);
                    display: flex; align-items: center; justify-content: center;
                }
            `}</style>

            {
                loading ?
                    <div className="pt-20 text-center">
                        <Indicator />
                    </div>
                    :
                    <div className="pt-2">

                        {
                            alwaysAvailable ?
                                <div className="text-center py-15">
                                    <div className="wh-always-badge mx-auto mb-5">
                                        <Icon icon="bolt" className="fs-3x text-success" />
                                    </div>
                                    <div className="fw-bold fs-2 text-gray-900 mb-2">Always available</div>
                                    <div className="fs-6 text-gray-500 mx-auto" style={{ maxWidth: 320 }}>
                                        This technician has no working-hours restriction — you can reach them any time.
                                    </div>
                                </div>
                                :
                                <>
                                    {/* Live local time */}
                                    <FlexRow className="mb-6">
                                        <Icon icon="clock" className="fs-2x text-gray-400 me-3" />
                                        <div className="d-flex flex-column">
                                            <span className="wh-time fw-bold text-gray-900" style={{ fontSize: "1.9rem", lineHeight: 1.1 }}>
                                                {liveTime ?? "--:--:--"}
                                            </span>
                                            <span className="fs-8 text-gray-500">
                                                {model?.timezoneName ?? model?.timezoneId ?? "Local time unavailable"}
                                            </span>
                                        </div>
                                    </FlexRow>

                                    {/* Availability status / countdown */}
                                    {
                                        openNow ?
                                            <div className="bg-light-success rounded px-4 py-3 mb-8 d-flex flex-column">
                                                <span className="fs-7 fw-bold text-success">Available now</span>
                                                {
                                                    secondsUntil != null &&
                                                    <span className="fs-8 text-gray-600 mt-1">
                                                        Finishes in <span className="wh-time fw-semibold">{formatDuration(secondsUntil)}</span>
                                                    </span>
                                                }
                                            </div>
                                            : secondsUntil != null ?
                                                <div className="bg-light-primary rounded px-4 py-3 mb-8 d-flex flex-column">
                                                    <span className="fs-8 text-gray-500">Starts working in</span>
                                                    <span className="wh-time fw-bold text-primary" style={{ fontSize: "1.5rem", lineHeight: 1.2 }}>
                                                        {formatDuration(secondsUntil)}
                                                    </span>
                                                </div>
                                                : <></>
                                    }

                                    {/* Weekly schedule */}
                                    <div>
                                        <div className="fs-8 fw-bold text-gray-400 text-uppercase mb-2">
                                            Weekly schedule
                                        </div>
                                        {
                                            WeekDays.map(d => {
                                                const w = model?.workingHours?.find(f => f.dayOfWeek == d.id)
                                                const isToday = techDay != null && techDay == d.id
                                                return (
                                                    <FlexRow key={`wh-day-${d.id}`} className="wh-day py-3">
                                                        <span className={isToday ? "fw-bold text-gray-900" : w ? "fw-semibold text-gray-800" : "fw-semibold text-gray-400"}>
                                                            {d.name}
                                                        </span>
                                                        {
                                                            isToday &&
                                                            <span className="fs-9 fw-bold text-primary ms-2">Today</span>
                                                        }
                                                        <div className="ms-auto">
                                                            {
                                                                w ?
                                                                    <span className="wh-time fs-7 fw-semibold text-gray-700">
                                                                        {w.fromTime} – {w.toTime}
                                                                    </span>
                                                                    :
                                                                    <span className="fs-8 text-gray-400">Closed</span>
                                                            }
                                                        </div>
                                                    </FlexRow>
                                                )
                                            })
                                        }
                                    </div>
                                </>
                        }

                    </div>
            }

        </Drawer>
    )
}
